/**
 * QueueTimelineEvents — the popover body: one row per lifecycle event,
 * newest first (icon · label · time · source badge · reason / rule pill).
 * Mounted only while the popover is open, so the fetch is on-demand.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { ElementEventRecord, FlowLayer } from '@trafficpulse/shared';

import { Badge, Skeleton } from '@/components/ui';
import { formatDateTimeGb } from '@/lib/utils';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { timelineEventMeta } from './timeline-meta';
import { useQueueEvents } from './useQueueEvents';

const EventRow: FC<{ event: ElementEventRecord }> = ({ event }) => {
  const meta = timelineEventMeta(event.event);
  return (
    <li className="flex items-start gap-2 rounded px-1 py-1">
      <meta.Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.iconClass}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5">
          <span className="text-xs font-medium">{meta.label}</span>
          <Badge tone="secondary" className="text-[9px] capitalize">
            {event.source}
          </Badge>
        </p>
        <p className="text-[10px] tabular-nums text-muted-foreground">
          {formatDateTimeGb(event.at)}
        </p>
        {event.reason ? (
          <p className="truncate text-[10px] text-muted-foreground" title={event.reason}>
            {event.reason}
            {event.ruleId ? (
              <span className="ml-1 rounded bg-muted px-1 py-0.5 font-mono text-[9px]">
                {event.ruleId}
              </span>
            ) : null}
          </p>
        ) : null}
      </div>
    </li>
  );
};

export interface QueueTimelineEventsProps {
  campaignId: string;
  provider: QueueProvider;
  layer: FlowLayer;
  bankId: string;
}

const QueueTimelineEvents: FC<QueueTimelineEventsProps> = (props) => {
  const query = useQueueEvents(props);
  if (query.isLoading) {
    return (
      <div className="space-y-1.5 p-1">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    );
  }
  if (query.isError) {
    return <p className="px-1 text-[11px] text-destructive">Could not load timeline.</p>;
  }
  const events = query.data?.events ?? [];
  if (events.length === 0) {
    return <p className="px-1 text-[11px] text-muted-foreground">No recorded events.</p>;
  }
  return (
    <ul className="max-h-72 space-y-0.5 overflow-y-auto">
      {events.map((event) => (
        <EventRow key={event.id} event={event} />
      ))}
    </ul>
  );
};

export default QueueTimelineEvents;
